import { useState, useEffect } from 'react';

/**
 * SafeImg — an <img> that quietly hides itself if the src 404s or otherwise
 * errors. Used for user-uploaded content (vendor covers, logos, gallery, etc.)
 * so missing/legacy image URLs don't render as broken-image icons in the UI.
 *
 * Props:
 *   src         — image URL (falsy values hide the img entirely)
 *   fallback    — optional React node to render if the image fails or src is empty
 *   onFail      — optional callback fired once when the image fails to load
 *   ...rest     — all standard <img> props (alt, className, style, etc.)
 */
export const SafeImg = ({ src, fallback = null, onFail, alt = '', ...rest }) => {
  const [failed, setFailed] = useState(false);

  // Reset error state if the URL changes (e.g., user uploads a new photo).
  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (!src || failed) return fallback;

  return (
    <img
      {...rest}
      src={src}
      alt={alt}
      onError={() => {
        setFailed(true);
        if (onFail) onFail();
      }}
    />
  );
};

export default SafeImg;
